/**
 * @module Header
 * @description Represents the header component of the application.
 * @returns {JSX.Element} The header component.
 */
import React, { useContext, lazy, Suspense } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navbar, Nav, NavDropdown } from 'react-bootstrap'
import { useNavigate, Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRightFromBracket,
  faBuilding,
  faChartPie,
  faCode,
  faCreditCard,
  faEnvelope,
  faGaugeHigh,
  faLeaf,
  faMoon,
  faReceipt,
  faSun,
  faUser,
  faUserPlus,
  faUsers
} from '@fortawesome/free-solid-svg-icons'
import { ThemeContext } from './ThemeContext.js'
import './Header.css'

// Lazy load the SearchBox component to improve performance
const SearchBox = lazy(() => import('../components/SearchBox.js'))

// MenuItem component for rendering individual items in the header dropdown menus
const MenuItem = ({ icon, children, ...props }) => (
  <NavDropdown.Item {...props}>
    <FontAwesomeIcon
      className='header-menu-icon'
      icon={icon}
      aria-hidden='true'
    />
    <span>{children}</span>
  </NavDropdown.Item>
)

// Header component
const Header = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme, isDarkMode, setIsDarkMode } = useContext(ThemeContext)

  // Get login state
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin
  //
  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin
  const isPublicHeader = !userInfo && !ownerInfo

  const navbarStyle = {
    '--header-text': theme.text,
    '--header-subtext': theme.subtext,
    '--header-card': theme.card,
    '--header-border': theme.sidebarAccent,
    '--header-hover-bg':
      isDarkMode ? 'rgba(61, 217, 197, 0.12)' : 'rgba(15, 118, 110, 0.08)',
    '--header-active-bg':
      isDarkMode ? 'rgba(61, 217, 197, 0.18)' : 'rgba(15, 118, 110, 0.13)',
    '--header-top-hover-bg':
      isDarkMode ? 'rgba(61, 217, 197, 0.12)' : 'rgba(15, 118, 110, 0.08)',
    '--header-top-active-bg':
      isDarkMode ? 'rgba(61, 217, 197, 0.18)' : 'rgba(15, 118, 110, 0.13)',
    '--header-focus-ring':
      isDarkMode ? 'rgba(61, 217, 197, 0.3)' : 'rgba(15, 118, 110, 0.25)',
    backgroundColor: theme.card,
    borderColor: isDarkMode ? '#040404' : '#DFE6E9'
  }

  // Logout handler
  const logoutHandler = async () => {
    if (userInfo) {
      const id = userInfo._id
      const { logout } = await import('../actions/userActions.js')
      dispatch(logout(id))
    } else if (ownerInfo) {
      const id = ownerInfo._id
      const { logoutOwner } = await import('../actions/ownerActions.js')
      dispatch(logoutOwner(id))
      navigate('/')
    } else {
      navigate('/')
    }
    // Clear cookies
    document.cookie = 'jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
    // Clear session and localstorage
    if (userInfo) {
      sessionStorage.removeItem('userInfo')
      localStorage.removeItem('userInfo')
    } else {
      sessionStorage.removeItem('ownerInfo')
      localStorage.removeItem('ownerInfo')
    }
  }

  if (isPublicHeader) {
    return (
      <Navbar
        className='themed-header px-3 px-lg-4 py-3 border-bottom shadow-sm'
        variant={isDarkMode ? 'dark' : 'light'}
        style={navbarStyle}
        expand='lg'
      >
        <Navbar.Brand
          as={Link}
          to='/'
          className='header-brand'
          style={{ color: theme.text }}
        >
          <span className='header-brand-mark' style={{ color: theme.accent }}>
            <FontAwesomeIcon icon={faLeaf} aria-hidden='true' />
          </span>
          <span className='header-brand-copy'>
            <strong>Global Receipt</strong>
            <small>
              {ownerInfo ?
                'Business workspace'
              : 'Digital receipts, made simple'}
            </small>
          </span>
        </Navbar.Brand>

        <Navbar.Toggle aria-controls='basic-navbar-nav' />
        <Navbar.Collapse id='basic-navbar-nav'>
          <Nav className='header-actions ms-auto d-flex align-items-center gap-2'>
            <button
              className='header-theme-toggle'
              onClick={() => setIsDarkMode(!isDarkMode)}
              title={
                isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
              }
              aria-label={
                isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
              }
              aria-pressed={isDarkMode}
            >
              <FontAwesomeIcon
                icon={isDarkMode ? faSun : faMoon}
                aria-hidden='true'
              />
              <span>{isDarkMode ? 'Light' : 'Dark'}</span>
            </button>
            <NavDropdown
              title='Explore'
              id='basic-nav-dropdown'
              className='themed-nav-dropdown'
              menuVariant={isDarkMode ? 'dark' : undefined}
            >
              <NavDropdown.Header>For shoppers</NavDropdown.Header>
              <MenuItem as={Link} to='/login' icon={faUser}>
                Sign in
              </MenuItem>
              <MenuItem as={Link} to='/register' icon={faUserPlus}>
                Create an account
              </MenuItem>
              <NavDropdown.Divider />
              <NavDropdown.Header>For businesses</NavDropdown.Header>
              <MenuItem as={Link} to='/ownerlogin' icon={faBuilding}>
                Business sign in
              </MenuItem>
              <MenuItem as={Link} to='/businessregister' icon={faUserPlus}>
                Register a business
              </MenuItem>
              <NavDropdown.Divider />
              <MenuItem as={Link} to='/about' icon={faLeaf}>
                About us
              </MenuItem>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    )
  }

  return (
    <Navbar
      className='themed-header px-3 px-lg-4 py-3 border-bottom shadow-sm'
      variant={isDarkMode ? 'dark' : 'light'}
      style={navbarStyle}
      expand='lg'
    >
      <Navbar.Brand
        as={Link}
        to={ownerInfo ? '/ownerdashboard' : '/dashboard'}
        className='header-brand'
        style={{ color: theme.text }}
      >
        <span className='header-brand-mark' style={{ color: theme.accent }}>
          <FontAwesomeIcon icon={faLeaf} aria-hidden='true' />
        </span>
        <span className='header-brand-copy'>
          <strong>Global Receipt</strong>
          <small>Digital receipts, made simple</small>
        </span>
      </Navbar.Brand>

      <Navbar.Toggle aria-controls='basic-navbar-nav' />
      <Navbar.Collapse id='basic-navbar-nav'>
        {userInfo && (
          <Suspense fallback={null}>
            <SearchBox />
          </Suspense>
        )}

        <Nav className='header-actions ms-auto d-flex align-items-center gap-2'>
          <button
            className='header-theme-toggle'
            onClick={() => setIsDarkMode(!isDarkMode)}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={
              isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
            }
            aria-pressed={isDarkMode}
          >
            <FontAwesomeIcon
              icon={isDarkMode ? faSun : faMoon}
              aria-hidden='true'
            />
            <span>{isDarkMode ? 'Light' : 'Dark'}</span>
          </button>

          {(userInfo || ownerInfo) && (
            <Nav.Link
              as={Link}
              to={ownerInfo ? '/ownerdashboard' : '/dashboard'}
              className='header-dashboard-link'
            >
              <FontAwesomeIcon icon={faGaugeHigh} aria-hidden='true' />
              Dashboard
            </Nav.Link>
          )}

          {userInfo && (
            <Nav.Link as={Link} to='/receipts' className='header-receipts-link'>
              <FontAwesomeIcon icon={faReceipt} aria-hidden='true' />
              Receipts
            </Nav.Link>
          )}

          {userInfo && !userInfo.isAdmin && (
            <NavDropdown
              title={
                <span className='header-account-title'>
                  <FontAwesomeIcon icon={faUser} aria-hidden='true' />
                  {userInfo.name}
                </span>
              }
              id='username'
              className='themed-nav-dropdown'
              menuVariant={isDarkMode ? 'dark' : undefined}
            >
              <NavDropdown.Header>Your account</NavDropdown.Header>
              <MenuItem as={Link} to='/profile' icon={faUser}>
                Profile & preferences
              </MenuItem>
              <NavDropdown.Divider />
              <NavDropdown.Header>Your activity</NavDropdown.Header>
              <MenuItem as={Link} to='/receipts' icon={faReceipt}>
                My receipts
              </MenuItem>
              <MenuItem as={Link} to='/reports' icon={faChartPie}>
                Reports
              </MenuItem>
              <NavDropdown.Divider />
              <MenuItem href='mailto:info@globalreceipt.ca' icon={faEnvelope}>
                Contact Us
              </MenuItem>
              <MenuItem onClick={logoutHandler} icon={faArrowRightFromBracket}>
                Logout
              </MenuItem>
            </NavDropdown>
          )}

          {userInfo && userInfo.isAdmin && (
            <NavDropdown
              title={
                <span className='header-account-title'>
                  <FontAwesomeIcon icon={faUser} aria-hidden='true' />
                  {userInfo.name}
                </span>
              }
              id='username'
              className='themed-nav-dropdown'
              menuVariant={isDarkMode ? 'dark' : undefined}
            >
              <NavDropdown.Header>Administration</NavDropdown.Header>
              <MenuItem as={Link} to='/userlist' icon={faUsers}>
                Users
              </MenuItem>
              <NavDropdown.Divider />
              <NavDropdown.Header>Your account</NavDropdown.Header>
              <MenuItem as={Link} to='/profile' icon={faUser}>
                Profile & preferences
              </MenuItem>
              <NavDropdown.Divider />
              <NavDropdown.Header>Your activity</NavDropdown.Header>
              <MenuItem as={Link} to='/receipts' icon={faReceipt}>
                My receipts
              </MenuItem>
              <MenuItem as={Link} to='/reports' icon={faChartPie}>
                Reports
              </MenuItem>
              <NavDropdown.Divider />
              <MenuItem href='mailto:info@globalreceipt.ca' icon={faEnvelope}>
                Contact Us
              </MenuItem>
              <MenuItem onClick={logoutHandler} icon={faArrowRightFromBracket}>
                Logout
              </MenuItem>
            </NavDropdown>
          )}

          {ownerInfo && (
            <NavDropdown
              title={
                <span className='header-account-title'>
                  <FontAwesomeIcon icon={faBuilding} aria-hidden='true' />
                  {ownerInfo.name}
                </span>
              }
              id='ownername'
              className='themed-nav-dropdown'
              menuVariant={isDarkMode ? 'dark' : undefined}
            >
              <NavDropdown.Header>Business overview</NavDropdown.Header>
              <MenuItem as={Link} to='/owneranalytics' icon={faChartPie}>
                Analytics
              </MenuItem>
              <MenuItem as={Link} to='/receiptchart' icon={faReceipt}>
                Receipt Count Chart
              </MenuItem>
              <NavDropdown.Divider />
              <NavDropdown.Header>Business tools</NavDropdown.Header>
              <MenuItem as={Link} to='/ownerprofile' icon={faBuilding}>
                Business profile
              </MenuItem>
              <MenuItem as={Link} to='/payment' icon={faCreditCard}>
                Billing & Payments
              </MenuItem>
              <MenuItem as={Link} to='/apidocument' icon={faCode}>
                API documentation
              </MenuItem>
              <NavDropdown.Divider />
              <MenuItem href='mailto:info@globalreceipt.ca' icon={faEnvelope}>
                Contact Us
              </MenuItem>
              <MenuItem onClick={logoutHandler} icon={faArrowRightFromBracket}>
                Logout
              </MenuItem>
            </NavDropdown>
          )}
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  )
}

export default Header
